import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import NewCourseModal from '@/components/mentorship/NewCourseModal';
import CSVUploadModal from '@/components/mentorship/CSVUploadModal';
import ScheduledMeetingsList from '@/components/mentorship/ScheduledMeetingsList';
import { MoreVertical, Trash2, Type } from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuTrigger, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator 
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const MentorshipDashboard = () => {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("courses");
  
  // Modal states
  const [isNewCourseModalOpen, setIsNewCourseModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPrefixDialogOpen, setIsPrefixDialogOpen] = useState(false);
  const [selectedCourseForPrefix, setSelectedCourseForPrefix] = useState(null);
  const [newPrefix, setNewPrefix] = useState('');
  const [numberingType, setNumberingType] = useState('alphabetic');
  const [isUpdatingPrefix, setIsUpdatingPrefix] = useState(false);
  const [currentPrefix, setCurrentPrefix] = useState('');
  const [previewBatches, setPreviewBatches] = useState([]);

  // Course thumbnail mapping
  const courseThumbnails = {
    "NITYA PRAGATI - 1:1 Mentorship Program for UPSC CSE 2026": "pragti-course-mentorship.png",
    "Live GS Foundation Course 2026 - Batch 4 Plus": "foundation-2026-batch-4-plus.png",
    "Live GS Foundation Course 2026 - Batch 3 Plus": "gs-foundation-web-Thumbnail-plus.png",
    "Beginners' Kit - Understand UPSC before Starting UPSC Prep": "beginner-guide-2026-27.jpg",
    "NITYA PRAGATI - 1:1 Mentorship Program - Batch 2 for UPSC CSE 2026": "pragti-course-mentorship-batch-2.png"
  };

  const fetchCourses = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/mentorship/courses');
      setCourses(response.data.courses);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to fetch courses', {
        description: 'Please refresh the page to try again'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []); // Fetch courses on initial load

  const handleSuccess = () => {
     fetchCourses(); // Refetch courses after adding a new one or uploading CSV
     // Modals will close themselves or via their own logic
  };

  const handleDeleteCourse = async () => {
    if (!courseToDelete || deleteConfirmationText !== 'confirm') {
      return;
    }

    setIsDeleting(true);
    try {
      await axios.delete(
        `https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/mentorship/course/${encodeURIComponent(courseToDelete)}`
      );

      toast.success('Course deleted successfully', {
        description: `Course &quot;${courseToDelete}&quot; has been deleted`
      });
      setIsDeleteAlertOpen(false);
      fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course', {
        description: error.response?.data?.error || 'Please try again later'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = (courseName) => {
    setCourseToDelete(courseName);
    setIsDeleteAlertOpen(true);
  };

  // Function to open prefix change dialog
  const openPrefixDialog = async (courseName) => {
    setSelectedCourseForPrefix(courseName);
    setNewPrefix('');
    setNumberingType('alphabetic');
    setIsPrefixDialogOpen(true);

    try {
      const response = await axios.get(
        `https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/mentorship/course/${encodeURIComponent(courseName)}/batchCounts`
      );
      const batches = response.data.batchCounts;
      
      if (batches.length > 0) {
        const firstBatch = batches[0].SK;
        const usesHyphen = firstBatch.includes('-');
        const prefix = usesHyphen ? firstBatch.split('-')[0] : firstBatch.match(/^[A-Z]+/)[0];
        setCurrentPrefix(prefix);
        
        const isAlphabetic = usesHyphen ? 
          /^[A-Z]$/.test(firstBatch.split('-')[1]) : 
          /^[A-Z]\d+$/.test(firstBatch);
        setNumberingType(isAlphabetic ? 'alphabetic' : 'numeric');
        
        generatePreview(
          prefix, 
          isAlphabetic ? 'alphabetic' : 'numeric', 
          batches.length,
          usesHyphen ? 'hyphenated' : 'concatenated'
        );
      }
    } catch (error) {
      console.error('Error fetching current prefix:', error);
      toast.error('Failed to fetch current prefix', {
        description: error.response?.data?.error || 'Please try again later'
      });
    }
  };

  // Function to generate preview of new batch IDs
  const generatePreview = (prefix, type, count, currentFormat) => {
    const preview = [];
    const usesHyphen = currentFormat === 'hyphenated';
    
    for (let i = 0; i < count; i++) {
        if (type === 'alphabetic') {
            const suffix = String.fromCharCode(65 + i); // A, B, C, ...
            preview.push(usesHyphen ? `${prefix}-${suffix}` : `${prefix}${suffix}`);
        } else {
            const suffix = (i + 1).toString();
            preview.push(usesHyphen ? `${prefix}-${suffix}` : `${prefix}${suffix}`);
        }
    }
    setPreviewBatches(preview);
  };

  // Function to handle prefix change
  const handlePrefixChange = async () => {
    if (!newPrefix || !selectedCourseForPrefix) {
      toast.error('Invalid input', {
        description: 'Please provide a new prefix'
      });
      return;
    }

    setIsUpdatingPrefix(true);
    try {
      await axios.put(
        `https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/mentorship/course-update-prefix/${encodeURIComponent(selectedCourseForPrefix)}`,
        {
          newPrefix,
          numberingType
        }
      );

      toast.success('Course prefix updated successfully', {
        description: `Updated ${previewBatches.length} batches with new prefix "${newPrefix}"`
      });
      setIsPrefixDialogOpen(false);
      // Refresh the courses list
      fetchCourses();
    } catch (error) {
      console.error('Error updating prefix:', error);
      toast.error('Failed to update course prefix', {
        description: error.response?.data?.error || 'Please try again later'
      });
    } finally {
      setIsUpdatingPrefix(false);
    }
  };

  // Update preview when prefix or numbering type changes
  useEffect(() => {
    if (selectedCourseForPrefix && newPrefix && previewBatches.length > 0) {
        const usesHyphen = previewBatches[0].includes('-');
        generatePreview(
            newPrefix, 
            numberingType, 
            previewBatches.length,
            usesHyphen ? 'hyphenated' : 'concatenated'
        );
    }
  }, [newPrefix, numberingType]);

  // ✅ FIXED: Add cleanup for modal states
  const resetModalStates = () => {
    setIsNewCourseModalOpen(false);
    setIsUploadModalOpen(false);
    setCourseToDelete(null);
    setDeleteConfirmationText('');
    setIsDeleteAlertOpen(false);
    setIsDeleting(false);
    setIsPrefixDialogOpen(false);
    setSelectedCourseForPrefix(null);
    setNewPrefix('');
    setNumberingType('alphabetic');
    setIsUpdatingPrefix(false);
    setCurrentPrefix('');
    setPreviewBatches([]);
  };

  // ✅ FIXED: Add cleanup on unmount
  useEffect(() => {
    return () => {
      resetModalStates();
    };
  }, []);

  const renderLoadingSkeleton = () => (
    <div className="container mx-auto p-4 md:p-6 lg:p-8">
         <div className="flex justify-between items-center mb-6">
             <Skeleton className="h-9 w-1/3" />
             <div className="flex space-x-2">
                 <Skeleton className="h-10 w-36" />
                 <Skeleton className="h-10 w-32" />
             </div>
         </div>
         <Skeleton className="h-8 w-1/4 mb-6" />
         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
             {[...Array(3)].map((_, i) => (
                 <Card key={i}>
                     <CardHeader>
                         <Skeleton className="h-6 w-3/4" />
                     </CardHeader>
                     <CardContent>
                         <Skeleton className="h-4 w-full mb-2" />
                         <Skeleton className="h-4 w-2/3" />
                     </CardContent>
                     <CardFooter>
                         <Skeleton className="h-10 w-28" />
                     </CardFooter>
                 </Card>
             ))}
         </div>
     </div>
  );

  if (isLoading) {
    return renderLoadingSkeleton();
  }

  return (
    <>
      <Toaster 
        position="top-center" 
        expand={true}
        richColors
        closeButton
      />

      <div className="container mx-auto p-4 md:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
          <h1 className="text-2xl font-bold">Mentorship Dashboard</h1>
          <div className="flex space-x-2">
            <Button onClick={() => setIsNewCourseModalOpen(true)}>
              Create New Course
            </Button>
            <Button variant="outline" onClick={() => setIsUploadModalOpen(true)}>
              Upload CSV
            </Button>
          </div>
        </div>

        {error && (
            <Alert variant="destructive" className="mb-6">
                 <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        )}

        <Tabs 
          value={activeTab} 
          onValueChange={setActiveTab} 
          className="w-full mb-6"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="courses">Courses</TabsTrigger>
            <TabsTrigger value="meetings">Scheduled Meetings</TabsTrigger>
          </TabsList>

          <TabsContent value="courses">
            <h2 className="text-xl font-semibold mb-4">Existing Courses</h2>

            {courses.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.map((courseName) => (
                  <Card key={courseName} className="overflow-hidden flex flex-col">
                    <div className="w-full p-4 bg-gray-50 min-h-[200px] flex items-center justify-center">
                      <img
                        src={`/mentorship/course-thumnail/${courseThumbnails[courseName] || 'placeholder-course.jpg'}`}
                        alt={courseName}
                        className="w-full h-auto max-h-[180px] object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/mentorship/course-thumnail/placeholder-course.jpg';
                        }}
                      />
                    </div>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="truncate my-0">{courseName}</CardTitle>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                            <span className="sr-only">More</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="flex items-center"
                            onClick={() => openPrefixDialog(courseName)}
                          >
                            <Type className="mr-2 h-4 w-4" />
                            Change Course Prefix
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-destructive focus:text-destructive flex items-center"
                            onClick={() => openDeleteDialog(courseName)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </CardHeader>
                    <CardContent className="flex-grow">
                      <p className="text-sm text-muted-foreground">Manage batches and students for this course.</p>
                    </CardContent>
                    <CardFooter className="pt-4">
                      <Link to={`/mentorship/course/${encodeURIComponent(courseName)}`} className="w-full">
                        <Button className="w-full">View Details</Button>
                      </Link>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 border border-dashed rounded-lg">
                  <p className="text-muted-foreground">No courses found.</p>
                  <p className="text-sm text-muted-foreground">Create a new course or upload a CSV to get started.</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="meetings">
            <ScheduledMeetingsList />
          </TabsContent>
        </Tabs>

        {/* Modals need to be controlled via the open/onOpenChange props pattern */}
         <NewCourseModal
             open={isNewCourseModalOpen}
             onOpenChange={setIsNewCourseModalOpen} // Function to update the state
             onSuccess={() => {
                 handleSuccess();
                 setIsNewCourseModalOpen(false); // Close modal on success
             }}
         />

         <CSVUploadModal
             open={isUploadModalOpen}
             onOpenChange={setIsUploadModalOpen}
             onSuccess={() => {
                 handleSuccess();
                 setIsUploadModalOpen(false); // Close modal on success
             }}
             // Pass available courses if needed by the modal for selection
             availableCourses={courses}
         />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure you want to delete this course?</AlertDialogTitle>
              <AlertDialogDescription>
                This action will permanently delete the course <strong>{courseToDelete}</strong> and all associated data, 
                including all student records, batch assignments, and course details. This action cannot be undone.
                <div className="mt-4">
                  <p className="mb-2 font-medium">Type &quot;confirm&quot; to proceed:</p>
                  <Input 
                    value={deleteConfirmationText} 
                    onChange={(e) => setDeleteConfirmationText(e.target.value)}
                    placeholder="confirm"
                    className="mb-2"
                  />
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteCourse}
                disabled={deleteConfirmationText !== 'confirm' || isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Add the prefix change dialog */}
        <Dialog open={isPrefixDialogOpen} onOpenChange={setIsPrefixDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Change Course Prefix</DialogTitle>
              <DialogDescription>
                Update the course prefix and batch numbering type. This will affect all batches and students.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Current Prefix</Label>
                <Input value={currentPrefix} disabled />
              </div>
              <div className="grid gap-2">
                <Label>New Prefix</Label>
                <Input 
                  value={newPrefix} 
                  onChange={(e) => setNewPrefix(e.target.value)}
                  placeholder="Enter new prefix (e.g., warrior)"
                />
              </div>
              <div className="grid gap-2">
                <Label>Numbering Type</Label>
                <RadioGroup 
                  value={numberingType} 
                  onValueChange={setNumberingType}
                  className="flex flex-col space-y-1"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="alphabetic" id="alphabetic" />
                    <Label htmlFor="alphabetic" className="flex items-center">
                      <Type className="mr-2 h-4 w-4" />
                      Alphabetic (A, B, C, ...)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="numeric" id="numeric" />
                    <Label htmlFor="numeric" className="flex items-center">
                      <Type className="mr-2 h-4 w-4" />
                      Numeric (1, 2, 3, ...)
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              {previewBatches.length > 0 && (
                <div className="grid gap-2">
                  <Label>Preview of New Batch IDs</Label>
                  <div className="p-2 bg-muted rounded-md">
                    {previewBatches.map((batch, index) => (
                      <div key={index} className="text-sm">
                        {batch}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsPrefixDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePrefixChange}
                disabled={!newPrefix || isUpdatingPrefix}
              >
                {isUpdatingPrefix ? 'Updating...' : 'Update Prefix'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default MentorshipDashboard; 